import { CONVERSATIONAL_MM_AGENT, SPROUT_AGENT } from './agents';

export const TOXICITY_SCORER_ID = 'toxicity-scorer';
export const NO_TOOL_ERRORS_SCORER_ID = 'check-no-tool-errors';
export const CMM_JUDGE_SCORER_ID = 'cmm-judge';
/**
 * Deterministic (zero-LLM) bundle of transcript-level conversation-health checks
 * (liveness, no-loop, step completeness, nudge-free, no duplicate milestones,
 * tool-error-free). Continuous: score = fraction of applicable checks passed;
 * the per-check breakdown lives in the score's reason.
 */
export const CMM_CHECKS_SCORER_ID = 'cmm-checks';
export const SPROUT_JUDGE_SCORER_ID = 'sprout-judge';

/** Thread-level scorers shared across agents. */
export const UNIVERSAL_THREAD_SCORER_IDS = [] as const;

export const CMM_THREAD_SCORER_IDS = [
    CMM_JUDGE_SCORER_ID,
    CMM_CHECKS_SCORER_ID,
] as const;

export const SPROUT_THREAD_SCORER_IDS = [SPROUT_JUDGE_SCORER_ID] as const;

export const BINARY_THREAD_SCORER_IDS = [NO_TOOL_ERRORS_SCORER_ID] as const;

/**
 * Continuous scorers (shown as a percentage) that only "pass" at a perfect
 * score — anything below 100% is a fail/red. Used for the deterministic checks
 * bundle, where every applicable sub-check is expected to pass.
 */
export const PERFECT_ONLY_SCORER_IDS = [CMM_CHECKS_SCORER_ID] as const;

/** Continuous scorers where a lower score is better (e.g. toxicity). */
export const LOWER_IS_BETTER_SCORER_IDS = [TOXICITY_SCORER_ID] as const;

/** Pass threshold for continuous 0–1 quality scorers (higher is better). */
export const CONTINUOUS_SCORER_PASS_THRESHOLD = 0.8;

/**
 * Pass threshold for lower-is-better continuous scorers (e.g. toxicity).
 * Matches the Grove green band (`score <= 0.2`).
 */
export const LOWER_IS_BETTER_SCORER_PASS_THRESHOLD = 0.2;

export type ThreadScorerAssessment = 'pass' | 'fail';

const isBinaryScorerId = (scorerId: string): boolean =>
    (BINARY_THREAD_SCORER_IDS as readonly string[]).includes(scorerId);

const isLowerIsBetterScorerId = (scorerId: string): boolean =>
    (LOWER_IS_BETTER_SCORER_IDS as readonly string[]).includes(scorerId);

export const isPerfectOnlyScorerId = (scorerId: string): boolean =>
    (PERFECT_ONLY_SCORER_IDS as readonly string[]).includes(scorerId);

export const isContinuousScorerPassing = (score: number): boolean =>
    score >= CONTINUOUS_SCORER_PASS_THRESHOLD;

/**
 * Maps a 0–1 scorer result to pass/fail for Datadog assessments and Grove UI.
 * Binary: exact 1 = pass. Lower-is-better: score at/under threshold = pass.
 * Otherwise: continuous quality threshold (higher is better).
 */
export const getThreadScorerAssessment = (
    scorerId: string,
    score: number,
): ThreadScorerAssessment => {
    if (isBinaryScorerId(scorerId)) {
        return score >= 1 ? 'pass' : 'fail';
    }

    if (isLowerIsBetterScorerId(scorerId)) {
        return score <= LOWER_IS_BETTER_SCORER_PASS_THRESHOLD ? 'pass' : 'fail';
    }

    if (isPerfectOnlyScorerId(scorerId)) {
        return score >= 1 ? 'pass' : 'fail';
    }

    return isContinuousScorerPassing(score) ? 'pass' : 'fail';
};

/** Scorer ids run for a full-thread scoring pass. */
export const getThreadScorerIdsForAgent = (
    agentId: string,
): readonly string[] => {
    if (agentId === CONVERSATIONAL_MM_AGENT.id) {
        return [...UNIVERSAL_THREAD_SCORER_IDS, ...CMM_THREAD_SCORER_IDS];
    }

    if (agentId === SPROUT_AGENT.id) {
        return [...UNIVERSAL_THREAD_SCORER_IDS, ...SPROUT_THREAD_SCORER_IDS];
    }

    return [...UNIVERSAL_THREAD_SCORER_IDS];
};
